import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import WorkItemIcon from '@atlaskit/icon/core/work-item';
import { Box, Flex, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { JsmAtlassianProjectUpdateJsmQueueGroupPayload } from '@atlassian/conversation-assistant-message-actions-api/jsm';
import { ChatGenericError } from '@atlassian/conversation-assistant-ui-components/ui/ChatGenericError';

import { messages } from './messages';
import { UpdateQueueGroupCardSuccess } from './UpdateQueueGroupCardSuccess';

type UpdateQueueGroupCardPayload = Omit<JsmAtlassianProjectUpdateJsmQueueGroupPayload, 'queues'> & {
	queues?: JsmAtlassianProjectUpdateJsmQueueGroupPayload['queues'];
};

const EMPTY_QUEUES: NonNullable<UpdateQueueGroupCardPayload['queues']> = [];
const MAX_BODY_HEIGHT_BEFORE_TRUNCATE_PX = 242;

const styles = cssMap({
	outermostWrapper: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: token('space.150'),
		width: '100%',
		boxSizing: 'border-box',
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	outerColumn: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		width: '100%',
		boxSizing: 'border-box',
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.raised'),
	},
	titleSection: {
		display: 'flex',
		alignItems: 'center',
		alignSelf: 'stretch',
		gap: token('space.150'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	titleSection_collapsedEnd: {
		borderBottomLeftRadius: token('radius.large'),
		borderBottomRightRadius: token('radius.large'),
	},
	headerWorkItemIconWrap: {
		backgroundColor: token('elevation.surface'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
	},
	contentSection: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		alignSelf: 'stretch',
		boxSizing: 'border-box',
		gap: token('space.150'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	contentSection_clamped: {
		maxHeight: `${MAX_BODY_HEIGHT_BEFORE_TRUNCATE_PX}px`,
		overflowY: 'hidden',
	},
	contentSectionOuter: {
		position: 'relative',
		width: '100%',
		alignSelf: 'stretch',
	},
	fadeOffZone: {
		position: 'absolute',
		left: 0,
		right: 0,
		bottom: 0,
		height: '32px',
		width: '100%',
		backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0), ${token('elevation.surface')} 100%)`,
		zIndex: 1,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	showMoreButton: {
		borderRadius: token('radius.xlarge'),
		boxShadow: token('elevation.shadow.overlay'),
		font: token('font.body.small'),
		fontWeight: token('font.weight.medium'),
		color: token('color.text.subtle'),
		backgroundColor: token('elevation.surface'),
		paddingBlock: token('space.050'),
		paddingInline: token('space.150'),
		zIndex: 100,
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
		'&:focus-visible': {
			backgroundColor: token('elevation.surface.hovered'),
		},
		'&:active': {
			backgroundColor: token('elevation.surface.pressed'),
		},
	},
	footerSection: {
		display: 'flex',
		justifyContent: 'flex-end',
		alignItems: 'center',
		alignSelf: 'stretch',
		gap: token('space.100'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
		backgroundColor: token('elevation.surface.raised'),
		borderBottomLeftRadius: token('radius.large'),
		borderBottomRightRadius: token('radius.large'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	headingWrapper: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		minWidth: '0px',
	},
	expandToggleWrap: {
		marginInlineStart: 'auto',
	},
	queueList: {
		marginBlockStart: '0',
		marginBlockEnd: '0',
		paddingInlineStart: token('space.250'),
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.075'),
	},
});

const ShowMoreButton = ({ onExpand }: { onExpand: () => void }): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={styles.fadeOffZone}>
			<Pressable
				xcss={styles.showMoreButton}
				onClick={onExpand}
				testId="update-queue-group-card.expand-body-content"
			>
				{formatMessage(messages.expandBodyContent)}
			</Pressable>
		</Box>
	);
};

export type UpdateQueueGroupCardProps = {
	payload: UpdateQueueGroupCardPayload;
	onNext: () => void;
	isNextLoading?: boolean;
	isNextDisabled?: boolean;
	isSuccess?: boolean;
};

export const UpdateQueueGroupCard = ({
	payload,
	onNext,
	isNextLoading = false,
	isNextDisabled = false,
	isSuccess = false,
}: UpdateQueueGroupCardProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isExpanded, setIsExpanded] = useState(true);
	const [hasUserExpandedBody, setHasUserExpandedBody] = useState(false);
	const [hasTruncatableBodyContent, setHasTruncatableBodyContent] = useState(false);
	const contentSectionRef = useRef<HTMLDivElement>(null);

	const ExpandCollapseIcon = isExpanded ? ChevronDownIcon : ChevronRightIcon;
	const showExpandedBody = !isSuccess && isExpanded;

	const heading = formatMessage(messages.cardTitle);
	const queues = payload.queues ?? EMPTY_QUEUES;
	const hasQueues = queues.length > 0;

	const measureBodyOverflow = useCallback(() => {
		const el = contentSectionRef.current;
		if (!el) {
			return;
		}
		setHasTruncatableBodyContent(el.scrollHeight > MAX_BODY_HEIGHT_BEFORE_TRUNCATE_PX);
	}, []);

	useEffect(() => {
		setHasUserExpandedBody(false);
	}, [payload.name, queues]);

	useLayoutEffect(() => {
		measureBodyOverflow();
	}, [measureBodyOverflow, showExpandedBody, payload.name, queues]);

	useLayoutEffect(() => {
		const el = contentSectionRef.current;
		if (!el || typeof ResizeObserver === 'undefined') {
			return undefined;
		}
		const observer = new ResizeObserver(() => {
			measureBodyOverflow();
		});
		observer.observe(el);
		return () => {
			observer.disconnect();
		};
	}, [measureBodyOverflow]);

	const isClamped = hasTruncatableBodyContent && !hasUserExpandedBody;

	const handleExpandBody = useCallback(() => {
		setHasUserExpandedBody(true);
	}, []);

	return (
		<ErrorBoundary fallback={<ChatGenericError errorType="generic" />}>
			<Box xcss={styles.outermostWrapper} testId="update-queue-group-card.root">
				{isSuccess ? (
					<UpdateQueueGroupCardSuccess
						heading={heading}
						successMessage={formatMessage(messages.successPill, { name: payload.name })}
					/>
				) : (
					<Box xcss={styles.outerColumn} testId="update-queue-group-card">
						<Flex xcss={cx(styles.titleSection, !isExpanded && styles.titleSection_collapsedEnd)}>
							<Flex
								xcss={styles.headerWorkItemIconWrap}
								alignItems="center"
								justifyContent="center"
							>
								<WorkItemIcon label="" spacing="none" color={token('color.icon.accent.blue')} />
							</Flex>
							<Box xcss={styles.headingWrapper}>
								<Heading size="xsmall">{heading}</Heading>
							</Box>
							<Box xcss={styles.expandToggleWrap}>
								<IconButton
									icon={ExpandCollapseIcon}
									label={formatMessage(
										isExpanded ? messages.collapseBodyContent : messages.expandBodyContent,
									)}
									appearance="subtle"
									spacing="compact"
									onClick={() => setIsExpanded(!isExpanded)}
									testId="update-queue-group-card.expand-toggle"
								/>
							</Box>
						</Flex>

						{showExpandedBody ? (
							<Box xcss={styles.contentSectionOuter}>
								<Box
									ref={contentSectionRef}
									xcss={cx(styles.contentSection, isClamped && styles.contentSection_clamped)}
								>
									<Stack space="space.200">
										<Heading size="medium">{payload.name}</Heading>
										{hasQueues ? (
											<Stack space="space.100">
												<Text size="medium">{formatMessage(messages.queuesLabel)}</Text>
												<Box as="ul" xcss={styles.queueList}>
													{queues.map((queue) => (
														<Box as="li" key={queue.id}>
															<Text size="medium">{queue.name}</Text>
														</Box>
													))}
												</Box>
											</Stack>
										) : (
											<Text size="medium">{formatMessage(messages.emptyQueuesDescription)}</Text>
										)}
									</Stack>
								</Box>
								{isClamped ? <ShowMoreButton onExpand={handleExpandBody} /> : null}
							</Box>
						) : null}

						{showExpandedBody ? (
							<Flex xcss={styles.footerSection}>
								<Button
									appearance="primary"
									onClick={onNext}
									isLoading={isNextLoading}
									isDisabled={isNextDisabled}
									testId="update-queue-group-card.next"
								>
									{formatMessage(messages.confirmButton)}
								</Button>
							</Flex>
						) : null}
					</Box>
				)}
			</Box>
		</ErrorBoundary>
	);
};
